import { useState, useEffect, useMemo } from "react";
import { CheckIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { FloatingPanel } from "@/components/ui/floating-panel";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PAGE_SIZE_PRESETS,
  ARCHITECTURAL_PRESETS,
  calibrateFromPageSizeAndScale,
  detectPageSize,
  type PageSizePreset,
  type PresetScale,
} from "@/lib/geometry";
import { cn } from "@/lib/utils";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import { VIEWPORT_TOOL_PRESSED_CLASSES } from "@/viewport-tools/core";

function parsePositiveNumber(value: string): number | null {
  const trimmed = value.trim().replace(/"/g, "");
  if (!trimmed) return null;
  const mixedMatch = trimmed.match(
    /^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/
  );
  if (mixedMatch) {
    const whole = Number(mixedMatch[1]);
    const numerator = Number(mixedMatch[2]);
    const denominator = Number(mixedMatch[3]);
    const parsed = denominator > 0 ? whole + numerator / denominator : null;
    return parsed && parsed > 0 ? parsed : null;
  }
  const fractionMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
  if (fractionMatch) {
    const numerator = Number(fractionMatch[1]);
    const denominator = Number(fractionMatch[2]);
    return denominator > 0 ? numerator / denominator : null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function parseFeet(value: string): number | null {
  const trimmed = value.trim();
  const feetInchesMatch = trimmed.match(
    /^(\d+(?:\.\d+)?)\s*'\s*-?\s*((?:\d+(?:\.\d+)?\s+)?\d+(?:\.\d+)?(?:\s*\/\s*\d+(?:\.\d+)?)?)?\s*"?$/
  );
  if (feetInchesMatch) {
    const feet = Number(feetInchesMatch[1]);
    const inches = feetInchesMatch[2] ? parsePositiveNumber(feetInchesMatch[2]) ?? 0 : 0;
    return feet + inches / 12;
  }
  return parsePositiveNumber(trimmed);
}

function isCompleteDrawingScale(value: string): boolean {
  return /^\d+(?:\.\d+)?(?:\s+\d+(?:\.\d+)?\s*\/\s*\d+(?:\.\d+)?|\s*\/\s*\d+(?:\.\d+)?)?"$/.test(value.trim());
}

function isCompleteRealWorldDistance(value: string): boolean {
  return /^\d+(?:\.\d+)?'\s*-?\s*(?:(?:\d+(?:\.\d+)?\s+)?\d+(?:\.\d+)?(?:\s*\/\s*\d+(?:\.\d+)?)?)?\s*"?$/.test(value.trim());
}

function getCustomScale(drawingInches: string, realWorldDistance: string): PresetScale | null {
  if (!isCompleteDrawingScale(drawingInches) || !isCompleteRealWorldDistance(realWorldDistance)) {
    return null;
  }
  const paperInches = parsePositiveNumber(drawingInches);
  const realFeet = parseFeet(realWorldDistance);
  if (!paperInches || !realFeet) return null;
  const label = `${drawingInches.trim()} = ${realWorldDistance.trim()}`;
  return { label, ratio: label.replace(/\s+/g, ""), inchesPerFoot: paperInches / realFeet };
}

function isValidDrawingScaleInput(value: string): boolean {
  return /^\d*(?:\.\d*)?(?:\s+\d*(?:\.\d*)?\s*\/\s*\d*(?:\.\d*)?|\s*\/\s*\d*(?:\.\d*)?)?"?$/.test(value);
}

function isValidRealWorldDistanceInput(value: string): boolean {
  return /^\d*(?:\.\d*)?'?(?:\s*-?\s*(?:\d*(?:\.\d*)?\s+)?\d*(?:\.\d*)?(?:\s*\/\s*\d*)?"?)?$/.test(value);
}

function normalizeScaleRatio(value: string): string {
  return value
    .replace(/[′’]/g, "'")
    .replace(/[″”]/g, '"')
    .replace(/\s+/g, "");
}

function formatScaleRatio(value: string): string {
  const normalized = normalizeScaleRatio(value);
  const [drawingInches, realWorldDistance] = normalized.split("=");
  return drawingInches && realWorldDistance
    ? `${drawingInches} = ${realWorldDistance}`
    : value;
}

function getCustomScaleInputs(value: string): {
  drawingInches: string;
  realWorldDistance: string;
} | null {
  const [drawingInches, realWorldDistance] = normalizeScaleRatio(value).split("=");
  if (!drawingInches || !realWorldDistance) return null;
  return { drawingInches, realWorldDistance };
}

function hasScaleChanged(
  currentScale: MeasurementScale | null | undefined,
  nextScale: PresetScale,
  pageWidthInches: number,
  pageAspectRatio: number | undefined
): boolean {
  if (!currentScale?.presetValue) {
    return true;
  }

  const currentRatio = normalizeScaleRatio(currentScale.presetValue);
  const nextRatio = normalizeScaleRatio(nextScale.ratio);
  if (currentRatio !== nextRatio) {
    return true;
  }

  const currentPageAspectRatio = currentScale.pageAspectRatio;
  if (
    Number.isFinite(currentPageAspectRatio) &&
    Number.isFinite(pageAspectRatio) &&
    Math.abs((currentPageAspectRatio as number) - (pageAspectRatio as number)) > 1e-9
  ) {
    return true;
  }

  const { pixelsPerFoot } = calibrateFromPageSizeAndScale(
    pageWidthInches,
    nextScale
  );
  return Math.abs(currentScale.pixelsPerFoot - pixelsPerFoot) > 1e-9;
}

function getCurrentScaleSelection(currentScale?: MeasurementScale | null): {
  matchingPreset: PresetScale | null;
  customLabel: string | null;
  customInputs: { drawingInches: string; realWorldDistance: string } | null;
} {
  if (!currentScale) {
    return { matchingPreset: null, customLabel: null, customInputs: null };
  }

  if (currentScale.scaleType === "preset" && currentScale.presetValue) {
    const currentRatio = normalizeScaleRatio(currentScale.presetValue);
    const matchingPreset =
      ARCHITECTURAL_PRESETS.find(
        (preset) => normalizeScaleRatio(preset.ratio) === currentRatio
      ) ?? null;

    return {
      matchingPreset,
      customLabel: matchingPreset ? null : formatScaleRatio(currentScale.presetValue),
      customInputs: matchingPreset ? null : getCustomScaleInputs(currentScale.presetValue),
    };
  }

  return {
    matchingPreset: null,
    customLabel: "Manual calibration",
    customInputs: null,
  };
}

interface PresetCalibrationPanelProps {
  currentScale?: MeasurementScale | null;
  pagePixelWidth: number;
  pagePixelHeight: number;
  onApplyPreset: (preset: PresetScale, pageWidthInches: number) => void;
  onCancel: () => void;
}

export function PresetCalibrationPanel({
  currentScale,
  pagePixelWidth,
  pagePixelHeight,
  onApplyPreset,
  onCancel,
}: PresetCalibrationPanelProps) {
  const [selectedPageSize, setSelectedPageSize] = useState<PageSizePreset | null>(null);
  const [selectedScale, setSelectedScale] = useState<PresetScale | null>(null);
  const [customDrawingInches, setCustomDrawingInches] = useState("3/8\"");
  const [customRealWorldDistance, setCustomRealWorldDistance] = useState("1'-0\"");
  const [isCustomScale, setIsCustomScale] = useState(false);
  const currentScaleSelection = useMemo(
    () => getCurrentScaleSelection(currentScale),
    [currentScale]
  );

  useEffect(() => {
    const detected = detectPageSize(pagePixelWidth, pagePixelHeight, 2);
    setSelectedPageSize(detected);
  }, [pagePixelWidth, pagePixelHeight]);

  useEffect(() => {
    if (currentScaleSelection.matchingPreset) {
      setSelectedScale(currentScaleSelection.matchingPreset);
      setIsCustomScale(false);
      return;
    }

    if (currentScaleSelection.customLabel) {
      setSelectedScale(null);
      setIsCustomScale(currentScaleSelection.customInputs !== null);
      if (currentScaleSelection.customInputs) {
        setCustomDrawingInches(currentScaleSelection.customInputs.drawingInches);
        setCustomRealWorldDistance(currentScaleSelection.customInputs.realWorldDistance);
      }
      return;
    }

    setSelectedScale(null);
    setIsCustomScale(false);
  }, [currentScaleSelection]);

  const effectiveWidthInches = selectedPageSize?.widthInches ?? pagePixelWidth / 72;
  const pageAspectRatio =
    pagePixelWidth > 0 && pagePixelHeight > 0
      ? pagePixelHeight / pagePixelWidth
      : undefined;
  const customScale = getCustomScale(customDrawingInches, customRealWorldDistance);
  const scale = isCustomScale ? customScale : selectedScale;
  const canApply =
    effectiveWidthInches > 0 &&
    scale !== null &&
    hasScaleChanged(currentScale, scale, effectiveWidthInches, pageAspectRatio);

  const handleApply = () => {
    if (scale && effectiveWidthInches > 0) {
      onApplyPreset(scale, effectiveWidthInches);
    }
  };

  const handlePageSizeSelect = (preset: PageSizePreset | null) => {
    setSelectedPageSize(preset);
  };

  return (
    <FloatingPanel className="pointer-events-auto w-72" title="Preset Calibration">
      <div className="mb-3 space-y-1.5">
        <div className="text-xs text-muted-foreground">Page Size</div>
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="compact" className="flex-1 justify-start">
                {selectedPageSize ? (
                  <>
                    <CheckIcon className="size-3" />
                    {selectedPageSize.label}
                  </>
                ) : (
                  "Detected page width"
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
              <DropdownMenuItem
                onSelect={() => handlePageSizeSelect(null)}
                className={cn("text-xs", !selectedPageSize && "bg-muted")}
              >
                <CheckIcon
                  className={cn("size-3 opacity-0", !selectedPageSize && "opacity-100")}
                />
                Detected page width
              </DropdownMenuItem>
              {PAGE_SIZE_PRESETS.map((preset) => (
                <DropdownMenuItem
                  key={preset.label}
                  onSelect={() => handlePageSizeSelect(preset)}
                  className={cn(
                    "text-xs",
                    selectedPageSize?.label === preset.label && "bg-muted"
                  )}
                >
                  <CheckIcon
                    className={cn(
                      "size-3 opacity-0",
                      selectedPageSize?.label === preset.label && "opacity-100"
                    )}
                  />
                  {preset.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {selectedPageSize ? (
          <div className="text-[10px] text-muted-foreground">
            {selectedPageSize.widthInches}" × {selectedPageSize.heightInches}" ({pagePixelWidth} × {pagePixelHeight} pts)
          </div>
        ) : (
          <div className="text-[10px] text-muted-foreground">
            Using detected PDF width ({pagePixelWidth} pts)
          </div>
        )}
      </div>

      <div className="mb-3 space-y-1.5">
        <div className="text-xs text-muted-foreground">Scale</div>
        <div className="grid grid-cols-2 gap-1">
          {ARCHITECTURAL_PRESETS.map((preset) => {
            const isSelected = !isCustomScale && selectedScale?.label === preset.label;
            return (
              <Button
                key={preset.label}
                variant="outline"
                size="compact"
                className={VIEWPORT_TOOL_PRESSED_CLASSES}
                aria-pressed={isSelected}
                onClick={() => {
                  setSelectedScale(preset);
                  setIsCustomScale(false);
                }}
              >
                {preset.label}
              </Button>
            );
          })}
          <div className="col-span-2 flex items-center gap-2">
            <Button
              variant="outline"
              size="compact"
              className={cn("w-[calc(50%-0.125rem)]", VIEWPORT_TOOL_PRESSED_CLASSES)}
              aria-label={
                currentScaleSelection.customLabel
                  ? `Custom scale. Current: ${currentScaleSelection.customLabel}`
                  : "Custom scale"
              }
              aria-pressed={isCustomScale}
              onClick={() => setIsCustomScale(true)}
            >
              Custom
            </Button>
            {currentScaleSelection.customLabel ? (
              <span className="min-w-0 flex-1 truncate text-[10px] text-muted-foreground">
                Current: {currentScaleSelection.customLabel}
              </span>
            ) : null}
          </div>
        </div>
        {isCustomScale && (
          <div className="mt-2 flex items-center gap-1">
            <Input
              aria-label="Drawing scale inches"
              value={customDrawingInches}
              onChange={(event) => {
                if (isValidDrawingScaleInput(event.target.value)) {
                  setCustomDrawingInches(event.target.value);
                }
              }}
              className="h-7 w-20 text-xs"
              placeholder={'3/8"'}
            />
            <span className="text-xs text-muted-foreground">=</span>
            <Input
              aria-label="Real-world scale distance"
              value={customRealWorldDistance}
              onChange={(event) => {
                if (isValidRealWorldDistanceInput(event.target.value)) {
                  setCustomRealWorldDistance(event.target.value);
                }
              }}
              className="h-7 w-24 text-xs"
              placeholder={'1\'-0"'}
            />
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          className="flex-1"
          disabled={!canApply}
          onClick={handleApply}
        >
          Apply
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </FloatingPanel>
  );
}
